import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SuiBox from "../../../components/SuiBox";
import SuiTypography from "../../../components/SuiTypography";
import SuiButton from "../../../components/SuiButton";
import SuiInput from "../../../components/SuiInput";
import SuiAlert from "../../../components/SuiAlert";
import { Card, Grid, Switch, FormControlLabel, Divider } from "@mui/material";
import { DEFAULT_WHATSAPP_TEMPLATES } from "constant";
import { whatsappService } from "services/whatsapp";
import { userSelector } from "store/features/authSlice";

function WhatsAppSettings() {
  const user = useSelector(userSelector);
  
  const [settings, setSettings] = useState({
    isEnabled: false,
    testPhoneNumber: "",
    testCountryCode: "91"
  });
  
  const [templates, setTemplates] = useState(DEFAULT_WHATSAPP_TEMPLATES);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });

  useEffect(() => {
    loadSettings();
  }, []);

  // Early return if user data is not available (after all hooks)
  if (!user) {
    return (
      <SuiBox>
        <SuiTypography variant="h6" color="error">
          Loading user data...
        </SuiTypography>
      </SuiBox>
    );
  }

  const loadSettings = () => {
    const savedSettings = localStorage.getItem("whatsapp_settings");
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
    }
  };

  const saveSettings = () => {
    localStorage.setItem("whatsapp_settings", JSON.stringify(settings));
    setMessage({ type: "success", content: "Settings saved successfully!" });
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      // Test with a simple message send
      const result = await whatsappService.sendTextMessage({
        mobile: settings.testPhoneNumber,
        countryCode: settings.testCountryCode,
        wid: "16071",
        bodyValues: {
          name: "Test User"
        }
      });
      
      if (result.success) {
        setMessage({ type: "success", content: "Connection successful! Test message sent." });
      } else {
        setMessage({ type: "error", content: `Connection failed: ${result.error}` });
      }
    } catch (error) {
      setMessage({ type: "error", content: `Connection failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const sendTestMessage = async () => {
    if (!settings.testPhoneNumber) {
      setMessage({ type: "error", content: "Please enter test phone number" });
      return;
    }

    setLoading(true);
    try {
      const result = await whatsappService.sendTextMessage({
        mobile: settings.testPhoneNumber,
        countryCode: settings.testCountryCode,
        wid: "test_template", // You'll need to create a test template
        bodyValues: {
          name: "Test User"
        }
      });
      
      setMessage({ type: "success", content: "Test message sent successfully!" });
    } catch (error) {
      setMessage({ type: "error", content: `Test message failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = (templateId, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value
      }
    }));
  };

  const saveTemplates = () => {
    localStorage.setItem("whatsapp_templates", JSON.stringify(templates));
    setMessage({ type: "success", content: "Templates saved successfully!" });
  };

  const resetTemplates = () => {
    setTemplates(DEFAULT_WHATSAPP_TEMPLATES);
    setMessage({ type: "info", content: "Templates reset to default values" });
  };

  return (
    <SuiBox>
      <SuiBox mb={3}>
        <SuiTypography variant="h4" fontWeight="bold">
          WhatsApp Settings
        </SuiTypography>
        <SuiTypography variant="body2" color="text">
          Configure WhatsApp API settings and message templates
        </SuiTypography>
      </SuiBox>

      {message.content && (
        <SuiAlert
          color={message.type}
          dismissible
          onClose={() => setMessage({ type: "", content: "" })}
        >
          {message.content}
        </SuiAlert>
      )}

      <Grid container spacing={3}>
        {/* API Configuration */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 2 }}>
            <SuiTypography variant="h6" fontWeight="bold" mb={2}>
              API Configuration
            </SuiTypography>
            
            <SuiBox mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isEnabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, isEnabled: e.target.checked }))}
                  />
                }
                label="Enable WhatsApp Notifications"
              />
            </SuiBox>


            <SuiBox mb={2}>
              <SuiInput
                label="Test Phone Number"
                value={settings.testPhoneNumber}
                onChange={(e) => setSettings(prev => ({ ...prev, testPhoneNumber: e.target.value }))}
                placeholder="Enter phone number for testing"
                fullWidth
              />
            </SuiBox>

            <SuiBox mb={3}>
              <SuiInput
                label="Country Code"
                value={settings.testCountryCode}
                onChange={(e) => setSettings(prev => ({ ...prev, testCountryCode: e.target.value }))}
                placeholder="91"
                fullWidth
              />
            </SuiBox>

            <SuiBox display="flex" gap={2}>
              <SuiButton
                variant="contained"
                color="primary"
                onClick={saveSettings}
                disabled={loading}
              >
                Save Settings
              </SuiButton>
              
              <SuiButton
                variant="outlined"
                color="primary"
                onClick={testConnection}
                disabled={loading}
              >
                Test Connection
              </SuiButton>
              
              <SuiButton
                variant="outlined"
                color="secondary"
                onClick={sendTestMessage}
                disabled={loading}
              >
                Send Test Message
              </SuiButton>
            </SuiBox>

          </Card>
        </Grid>

        {/* Message Templates */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 2 }}>
            <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SuiTypography variant="h6" fontWeight="bold">
                Message Templates
              </SuiTypography>
              <SuiBox>
                <SuiButton
                  variant="outlined"
                  size="small"
                  onClick={resetTemplates}
                  sx={{ mr: 1 }}
                >
                  Reset
                </SuiButton>
                <SuiButton
                  variant="contained"
                  size="small"
                  onClick={saveTemplates}
                >
                  Save Templates
                </SuiButton>
              </SuiBox>
            </SuiBox>

            {Object.entries(templates).map(([key, template]) => (
              <SuiBox key={key} mb={3}>
                <SuiTypography variant="subtitle2" fontWeight="bold" mb={1}>
                  {template.name}
                </SuiTypography>
                <SuiInput
                  multiline
                  rows={3}
                  value={template.message}
                  onChange={(e) => updateTemplate(key, "message", e.target.value)}
                  placeholder="Enter message template"
                  fullWidth
                />
                <SuiTypography variant="caption" color="text" mt={1}>
                  Variables: {template.variables?.join(", ") || "None"}
                </SuiTypography>
                <Divider sx={{ mt: 2 }} />
              </SuiBox>
            ))}
          </Card>
        </Grid>

        {/* API Documentation */}
        <Grid item xs={12}>
          <Card sx={{ p: 3, borderRadius: 2 }}>
            <SuiTypography variant="h6" fontWeight="bold" mb={2}>
              Backend Configuration
            </SuiTypography>
            <SuiTypography variant="body2" color="text" mb={2}>
              WhatsApp API credentials are securely stored on the backend. Contact your administrator to configure the API settings.
            </SuiTypography>
            <SuiTypography variant="body2" color="text" mb={2}>
              Invoice WhatsApp now sends through Creware CallDesk
              (`fitify_send_invoice` on the Tech line). Diet-chart and reminder
              templates are not available until they are approved on CallDesk.
            </SuiTypography>
            <SuiTypography variant="body2" color="text">
              Invoices send from invoice history through the{" "}
              <strong>sendInvoiceWhatsApp</strong> Cloud Function, which calls
              CallDesk <code>/api/integrations/whatsapp/send</code>.
            </SuiTypography>
          </Card>
        </Grid>
      </Grid>
    </SuiBox>
  );
}

export default WhatsAppSettings;
